import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { MdChevronRight, MdKeyboardArrowDown, MdVisibility, MdVisibilityOff } from "react-icons/md"
import { IntlProvider, FormattedNumber } from "react-intl"
import { ContextoGeral } from "../../contextos/ContextoGeral"
import ProfissionalAPI from "../../servicos/ProfissionalAPI"
import {
	componenteSaldoVisivel,
	permiteAlternarSaldo,
	visaoEfetivaSaldo,
	visaoInicialSaldo,
} from "../../utils/exibicaoSaldoApp"
import { lerSaldoOculto, salvarSaldoOculto } from "../../utils/preferenciaSaldoOculto"
import styles from "./CardSaldoHome.module.css"

export default function CardSaldoHome() {
	const navigate = useNavigate()
	const { dados } = useContext(ContextoGeral)
	const raiz = useRef(null)
	const [payload, setPayload] = useState(null)
	const [visaoUsuario, setVisaoUsuario] = useState("diario")
	const [menuAberto, setMenuAberto] = useState(false)
	const [oculto, setOculto] = useState(() => lerSaldoOculto(dados?.idProf))

	useEffect(() => {
		setOculto(lerSaldoOculto(dados?.idProf))
	}, [dados?.idProf])

	useEffect(() => {
		let cancelado = false
		async function carregar() {
			if (!dados?.dominio || !dados?.idProf || !dados?.token) return
			try {
				const retorno = await ProfissionalAPI.buscarSaldo(dados)
				if (cancelado) return
				if (!retorno?.resultado) {
					setPayload({ exibicao: false })
					return
				}
				setPayload(retorno.dados || { exibicao: false })
				setVisaoUsuario(visaoInicialSaldo(retorno.dados?.modoExibicao))
			} catch {
				if (!cancelado) setPayload({ exibicao: false })
			}
		}
		carregar()
		return () => { cancelado = true }
	}, [dados?.dominio, dados?.idProf, dados?.token])

	useEffect(() => {
		if (!menuAberto) return undefined
		function fechar(ev) {
			if (raiz.current && !raiz.current.contains(ev.target)) {
				setMenuAberto(false)
			}
		}
		document.addEventListener("mousedown", fechar)
		document.addEventListener("touchstart", fechar)
		return () => {
			document.removeEventListener("mousedown", fechar)
			document.removeEventListener("touchstart", fechar)
		}
	}, [menuAberto])

	const modo = payload?.modoExibicao
	const visivel = payload?.exibicao !== false && componenteSaldoVisivel(modo)
	const visao = visaoEfetivaSaldo(modo, visaoUsuario)
	const podeTrocar = permiteAlternarSaldo(modo) && payload?.permiteAlternar !== false

	const valor = useMemo(() => {
		if (visao === "diario") return Number(payload?.saldoDiario ?? 0)
		return Number(payload?.saldoTotal ?? payload?.saldo ?? 0)
	}, [visao, payload])

	if (!payload || !visivel || !visao) return null

	const rotuloVisao = visao === "diario" ? "Hoje" : "Total"

	function alternarOlho(e) {
		e.stopPropagation()
		const proximo = !oculto
		setOculto(proximo)
		salvarSaldoOculto(dados?.idProf, proximo)
	}

	function abrirDetalhes() {
		setMenuAberto(false)
		navigate("/app/saldo/detalhes")
	}

	return (
		<div className={styles.wrap} ref={raiz}>
			<div className={styles.cartao}>
				<button
					type="button"
					className={styles.olho}
					aria-label={oculto ? "Exibir valor do saldo" : "Ocultar valor do saldo"}
					onClick={alternarOlho}
				>
					{oculto ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
				</button>

				{podeTrocar && (
					<button
						type="button"
						className={styles.setaBtn}
						aria-haspopup="listbox"
						aria-expanded={menuAberto}
						aria-label={`Tipo de saldo: ${rotuloVisao}. Trocar`}
						onClick={() => setMenuAberto((v) => !v)}
					>
						<MdKeyboardArrowDown size={20} aria-hidden />
					</button>
				)}

				<button
					type="button"
					className={styles.valorBtn}
					onClick={abrirDetalhes}
					aria-label={`${rotuloVisao}: ${oculto ? "oculto" : "abrir detalhes"}`}
				>
					<span className={oculto ? styles.valorOculto : styles.valor}>
						{oculto ? (
							"R$ ••••"
						) : (
							<IntlProvider locale="pt-BR">
								<FormattedNumber value={valor} style="currency" currency="BRL" />
							</IntlProvider>
						)}
					</span>
					<MdChevronRight className={styles.chevron} size={18} aria-hidden />
				</button>
			</div>

			{menuAberto && podeTrocar ? (
				<div className={styles.menu} role="listbox">
					<button
						type="button"
						role="option"
						aria-selected={visao === "diario"}
						onClick={() => { setVisaoUsuario("diario"); setMenuAberto(false) }}
					>
						Hoje
					</button>
					<button
						type="button"
						role="option"
						aria-selected={visao === "total"}
						onClick={() => { setVisaoUsuario("total"); setMenuAberto(false) }}
					>
						Total
					</button>
				</div>
			) : null}
		</div>
	)
}
